import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import dayjs, { Dayjs } from 'dayjs';

import { useCommunityInfoContext } from 'components/CommunityInfo/CommunityInfoContext';
import { BROCHURE_AND_DOCS_QUERY_KEY } from 'components/CommunityInfo/general/documents/constants';

import { SNAPSHOT_SELECTOR_SELECTION } from './constants';
import { SnapshotEditorContext } from './SnapshotEditorContext';
import SnapshotEditorDropdown from './SnapshotEditorDropdown';
import SnapshotEditorModal from './SnapshotEditorModal';
import ShopDataModal from '../../../components/ShopData/ShopDataModal';
import { SnapshotDataType } from '../../../types/snapshot';
import { SNAPSHOT_MODAL_STEP } from '../constants';
import { useSnapshotContext } from '../SnapshotContext';

interface Props {
  communityId: number;
}

const SnapshotEditor = ({ communityId }: Props) => {
  const { refetch } = useSnapshotContext();
  const { communityInfoRefetch } = useCommunityInfoContext();
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState(SNAPSHOT_MODAL_STEP.DEFAULT);
  const [selection, setSelection] = useState(SNAPSHOT_SELECTOR_SELECTION.ENTER_NEW_DATA);
  const [date, setDate] = useState<Dayjs>(dayjs().subtract(1, 'day'));
  const [selectedHistoricalSnapshot, setSelectedHistoricalSnapshot] = useState<SnapshotDataType | undefined>(undefined);
  const [selectedSnapshot, setSelectedSnapshot] = useState<SnapshotDataType | undefined>(undefined);

  const handleClose = () => {
    setSelection(SNAPSHOT_SELECTOR_SELECTION.ENTER_NEW_DATA);
    setStep(SNAPSHOT_MODAL_STEP.DEFAULT);
    setSelectedSnapshot(undefined);
    setDate(dayjs());
  };

  const handleCreate = () => {
    handleClose();
    refetch();
    communityInfoRefetch();
    queryClient.invalidateQueries(BROCHURE_AND_DOCS_QUERY_KEY);
  };

  useEffect(() => {
    if (searchParams.get('openSnapshotEditor') === '1') {
      setStep(SNAPSHOT_MODAL_STEP.DATA);
      searchParams.delete('openSnapshotEditor');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  return (
    <SnapshotEditorContext.Provider
      value={{
        communityId,
        step,
        selection,
        date,
        selectedSnapshot,
        selectedHistoricalSnapshot,
        setStep,
        setSelection,
        setDate,
        setSelectedSnapshot,
        setSelectedHistoricalSnapshot,
      }}
    >
      {step === SNAPSHOT_MODAL_STEP.EDITOR && <SnapshotEditorModal />}
      {step === SNAPSHOT_MODAL_STEP.DATA && (
        <ShopDataModal
          communityId={communityId}
          snapshot={selectedSnapshot}
          open={step === SNAPSHOT_MODAL_STEP.DATA}
          mode={selection}
          date={date}
          onCreate={handleCreate}
          onClose={handleClose}
        />
      )}
      <SnapshotEditorDropdown />
    </SnapshotEditorContext.Provider>
  );
};

export default SnapshotEditor;
